/**
 * The rentals a reply's rental buttons can act on: the rider's active ones,
 * read each time the agent opens the button menu (a rental started or ended
 * during the chat shows up), and the rental the chat is anchored to when it
 * has ended, which only "Open rental" offers.
 *
 * ant scopes both to the systems the admin may read: a rental elsewhere is
 * simply not there, and a getRental it refuses reads as no rental.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { RiderRental, fetchRentalContext, fetchRiderActiveRentals } from './api';

export interface RiderRentals {
  /**
   * 'idle' until the first load; 'unlinked' when the conversation names no
   * rider, so there is nothing to read. A reload keeps a loaded list on
   * screen, with `refreshing` set until it answers.
   */
  status: 'idle' | 'loading' | 'ready' | 'error' | 'unlinked';
  /** A reload of a loaded list is in flight: its choices may be stale. */
  refreshing: boolean;
  /** Newest first. */
  active: RiderRental[];
  /** The anchored rental, once it has ended; null otherwise. */
  endedAnchor: RiderRental | null;
  load: () => void;
}

type State = Omit<RiderRentals, 'load'>;

const IDLE: State = { status: 'idle', refreshing: false, active: [], endedAnchor: null };

export function useRiderRentals(
  riderId: string | null | undefined,
  anchoredRentalId: string | null | undefined
): RiderRentals {
  const [state, setState] = useState<State>(IDLE);
  /** Bumped per load and per rider: an answer for an older one is dropped. */
  const latest = useRef(0);
  /** The list was asked for: a new rider or anchor reads it again at once. */
  const wanted = useRef(false);

  const load = useCallback(() => {
    wanted.current = true;
    latest.current += 1;
    const request = latest.current;
    if (!riderId) {
      setState({ ...IDLE, status: 'unlinked' });
      return;
    }
    setState((s) =>
      s.status === 'ready' ? { ...s, refreshing: true } : { ...s, status: 'loading' }
    );
    (async () => {
      try {
        const active = await fetchRiderActiveRentals(riderId);
        // Only "Open rental" uses the ended anchor: failing to read it must
        // not cost the other rental buttons the active list.
        const anchor =
          anchoredRentalId && !active.some((r) => r.id === anchoredRentalId)
            ? await fetchRentalContext(anchoredRentalId).catch(() => null)
            : null;
        if (request !== latest.current) return;
        setState({
          status: 'ready',
          refreshing: false,
          active,
          endedAnchor: anchor?.endTime ? anchor : null,
        });
      } catch (_) {
        if (request === latest.current) setState({ ...IDLE, status: 'error' });
      }
    })();
  }, [riderId, anchoredRentalId]);

  // A new rider or anchor drops what was read for the old one; when the list
  // is on screen (the form is open), it is read again rather than left on
  // "Loading" until the menu opens next.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    setState(IDLE);
    if (wanted.current) loadRef.current();
    return () => {
      latest.current += 1;
    };
  }, [riderId, anchoredRentalId]);

  return { ...state, load };
}
